import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Bell, Loader2, CheckCheck, ArrowLeft, Archive } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { fmtDate } from "@/components/PageHeader";
import SwipeToRead from "@/components/notifications/SwipeToRead";
import { shortenNos } from "@/lib/caseNo";

function ParentNotifCard({ n, onRead }) {
  return (
    <SwipeToRead enabled={!n.read} onSwiped={() => onRead(n.id)} testId={`parent-notif-swipe-${n.id}`}>
      <Card className={`flex items-start gap-4 border-slate-200 p-4 ${n.read ? "" : "border-sky-200 bg-sky-50/60"}`} data-testid={`parent-notification-${n.id}`}>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${n.read ? "bg-slate-100 text-slate-400" : "bg-sky-100 text-sky-600"}`}>
          <Bell className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800">{n.title}</p>
          <p className="text-sm text-slate-500">{shortenNos(n.message)}</p>
          <p className="mt-1 text-xs text-slate-400">{fmtDate(n.created_at)}</p>
        </div>
      </Card>
    </SwipeToRead>
  );
}

export default function ParentStudentNotifications() {
  const { id } = useParams();
  const [items, setItems] = useState(null);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/parent/students/${id}/notifications`);
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { setItems([]); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const markRead = async (nid) => { await api.post(`/parent/students/${id}/notifications/${nid}/read`); load(); };
  const markAll = async () => { await api.post(`/parent/students/${id}/notifications/read-all`); load(); };
  const active = (items || []).filter((n) => !n.read);
  const archived = (items || []).filter((n) => n.read);

  return (
    <div data-testid="parent-student-notifications">
      <div className="mb-4 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{unread > 0 ? `${unread} unread` : "All caught up"} — every alert your student has received.</p>
        {unread > 0 && (
          <Button onClick={markAll} variant="outline" size="sm" className="rounded-full" data-testid="parent-notif-mark-all">
            <CheckCheck className="mr-2 h-4 w-4" /> Mark all read
          </Button>
        )}
      </div>
      {items === null ? (
        <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-slate-200 p-10 text-center text-sm text-slate-400" data-testid="parent-notifications-empty">No notifications yet.</Card>
      ) : (
        <div className="space-y-5" data-testid="parent-notifications-list">
          <section className="space-y-2" data-testid="parent-notifications-active">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500"><Bell className="h-3.5 w-3.5" /> Active ({active.length})</p>
            {active.length === 0
              ? <p className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">No unread notifications.</p>
              : <>
                  <p className="flex items-center gap-1.5 px-1 text-xs text-slate-400" data-testid="parent-notifications-swipe-hint"><ArrowLeft className="h-3.5 w-3.5" /> Swipe left to mark as read</p>
                  {active.map((n) => <ParentNotifCard key={n.id} n={n} onRead={markRead} />)}
                </>}
          </section>
          {archived.length > 0 && (
            <section className="space-y-2 opacity-90" data-testid="parent-notifications-archive">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500"><Archive className="h-3.5 w-3.5" /> Archive ({archived.length})</p>
              {archived.map((n) => <ParentNotifCard key={n.id} n={n} onRead={markRead} />)}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
